/**
 * Stray spaces in Markdown copy (Ivan, 2026-10-29). Two or more spaces at the end of a line are a Markdown line
 * break, and they are invisible in the editor: a line that happened to end in "  " broke early on the site, which read
 * as random. Authors use `\` for a break. When the author leaves the editor:
 *
 *   - trailing spaces that ARE making a break become `\`, so the page renders exactly as before and the break is now
 *     visible, and deletable. /funeral-cost's "**Burial Fee – From $1,650**  " lines rely on theirs;
 *   - every other trailing space, and runs of spaces between words, go. They render as nothing.
 *
 * Indentation (nested lists), fenced code and inline code spans are left alone.
 */
const FENCE = /^[ \t]*(```|~~~)/;
/** A line that starts a new block, so the line before it ends a paragraph and a break there would do nothing. A `\`
 *  there is worse than nothing: at the end of a block Markdown prints it. */
const BLOCK_START = /^[ \t]*(#{1,6}[ \t]|[-*+][ \t]|\d+[.)][ \t]|>|\||:::|```|~~~|<|(-{3,}|\*{3,}|_{3,})[ \t]*$)/;
/** Lines where a trailing `\` is printed rather than read as a break. */
const NO_BREAK = /^[ \t]*(#{1,6}([ \t]|$)|\||:::|<|(-{3,}|\*{3,}|_{3,}|={3,})[ \t]*$)/;
const QUOTE = /^[ \t]*>[ \t]?/;

/** Runs of spaces between words → one, outside `code` spans. */
function collapse(body: string): string {
  return body
    .split(/(`+[^`]*`+)/)
    .map((part, i) => (i % 2 ? part : part.replace(/ {2,}/g, ' ')))
    .join('');
}

/** Is `next` more of the same paragraph as `line`? Inside a quote, compare what follows the `>`. */
function continues(line: string, next: string | undefined): boolean {
  if (next === undefined || !next.trim()) return false;
  const rest = QUOTE.test(line) && QUOTE.test(next) ? next.replace(QUOTE, '') : next;
  return rest.trim() !== '' && !BLOCK_START.test(rest);
}

export function tidySpaces(md: string): string {
  const lines = md.split('\n');
  let inFence = false;
  return lines
    .map((line, i) => {
      if (FENCE.test(line)) {
        inFence = !inFence;
        return line;
      }
      if (inFence) return line;
      const [, indent, body, trail] = /^([ \t]*)(.*?)([ \t]*)$/.exec(line)!;
      if (!body) return '';
      const tidied = indent + collapse(body);
      const breaks = / {2,}$/.test(trail) && !body.endsWith('\\') && !NO_BREAK.test(line) && continues(line, lines[i + 1]);
      return breaks ? `${tidied}\\` : tidied;
    })
    .join('\n');
}
